import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { marbellaYachts, type MarbellaYacht } from "@/data/marbella-yachts";
import heroImage from "@/assets/marbella-yachts-hero.webp";

const ease = [0.22, 1, 0.36, 1] as const;

type FilterCategory = "all" | "superyacht" | "yacht" | "dayboat";

const MarbellaYachts = () => {
  const [activeFilter, setActiveFilter] = useState<FilterCategory>("all");

  const filteredYachts = activeFilter === "all"
    ? marbellaYachts
    : marbellaYachts.filter((y) => y.category === activeFilter);

  const filters: { label: string; value: FilterCategory }[] = [
    { label: "All Vessels", value: "all" },
    { label: "Superyachts", value: "superyacht" },
    { label: "Yachts", value: "yacht" },
    { label: "Day Boats", value: "dayboat" },
  ];

  const faqItems = [
    { q: "How does booking a yacht in Marbella work?", a: "Send us a message via WhatsApp or email with your preferred yacht, date and group size. Our team will check availability, confirm pricing and arrange every detail for your charter." },
    { q: "What is included in the charter price?", a: "All charters include the yacht, professional skipper or crew, insurance, and standard onboard amenities. Fuel is typically included for standard routes. Food, drinks and additional services can be arranged upon request." },
    { q: "How many guests can join?", a: "Most yachts accommodate between 9 and 12 guests, depending on the vessel. Exact capacities are listed per boat." },
    { q: "Where do the yachts depart from?", a: "Most yachts depart from Puerto Banús or Marbella port. Alternative departure points along the Costa del Sol can be arranged." },
    { q: "What routes are available?", a: "Popular routes include cruising along the Golden Mile, visiting nearby coves, heading towards Estepona or Benalmádena, and even day trips to Gibraltar or the coast of Morocco." },
    { q: "Can I book for a half day or full day?", a: "Yes. Most yachts offer 4-hour and 8-hour charters. Extended charters, sunset cruises and multi-day voyages can also be arranged." },
    { q: "What happens in case of bad weather?", a: "If weather conditions are unsuitable for safe navigation, the charter will be rescheduled to an alternative date. Our team will keep you informed and assist with rebooking." },
    { q: "Is the deposit refundable?", a: "If cancelled on the same day as the charter, the deposit is non-refundable. Other cancellation conditions are communicated at the time of booking." },
    { q: "Can food, drinks or entertainment be arranged?", a: "Absolutely. We can organize catering, premium beverages, DJs, decorations and any other bespoke requests to make your charter special." },
    { q: "Do I need a boating license?", a: "No. All charters include a professional captain and crew, so no license is required." },
  ];

  const leftFaq = faqItems.slice(0, 5);
  const rightFaq = faqItems.slice(5);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />

      {/* Hero */}
      <section className="relative h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Arzani Yachts Marbella" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/20 to-foreground/10" />
        </div>
        <div className="relative z-10 px-8 md:px-16 lg:px-20 pb-20 md:pb-28 max-w-2xl">
          <motion.span
            className="font-label text-primary-foreground/40 tracking-[0.3em] text-[9px] block mb-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 2, ease }}
          >
            Marbella · Season 2026
          </motion.span>
          <motion.h1
            className="font-display text-3xl md:text-5xl lg:text-5xl text-primary-foreground leading-[1] tracking-wide uppercase mb-6 flex items-baseline gap-3 md:gap-5"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 2, ease }}
          >
            <span>Arzani</span>
            <span>Yachts</span>
          </motion.h1>
          <motion.p
            className="font-body text-primary-foreground/50 text-sm leading-[1.9] max-w-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 2, ease }}
          >
            Private yacht charters along the Costa del Sol — from Puerto Banús to the shores of Gibraltar.
          </motion.p>
        </div>
      </section>

      {/* Intro */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">Our Fleet</span>
            <p className="font-body text-muted-foreground text-sm md:text-base leading-[1.9]">
              Explore our curated collection of {marbellaYachts.length} luxury yachts available for charter in Marbella.
              From intimate day cruisers to impressive superyachts, each vessel comes with experienced crew
              and departs from the iconic Puerto Banús marina.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="pb-8 bg-background">
        <div className="container-luxury">
          <div className="flex flex-wrap items-center gap-3">
            {filters.map((f) => (
              <button
                key={f.value}
                onClick={() => setActiveFilter(f.value)}
                className={`font-label text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 border transition-colors duration-500 ${
                  activeFilter === f.value
                    ? "bg-foreground text-background border-foreground"
                    : "border-foreground/20 text-foreground/60 hover:text-foreground hover:border-foreground/40"
                }`}
              >
                {f.label}
              </button>
            ))}
            <span className="font-body text-muted-foreground text-xs ml-auto">
              {filteredYachts.length} vessel{filteredYachts.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      </section>

      {/* Fleet Grid */}
      <section className="pb-20 md:pb-32 bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {filteredYachts.map((yacht, i) => (
              <motion.div
                key={yacht.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 1, delay: (i % 3) * 0.1, ease }}
              >
                <Link
                  to={`/yachts/marbella/${yacht.slug}`}
                  className="group block"
                >
                  <div className="aspect-[4/3] overflow-hidden mb-5">
                    <img
                      src={yacht.image}
                      alt={yacht.name}
                      className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="font-display-medium text-base md:text-lg text-foreground tracking-wide uppercase mb-1">
                    {yacht.name}
                  </h3>
                  <p className="font-accent text-muted-foreground text-sm italic mb-2">
                    "{yacht.boatName}"
                  </p>
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <span className="font-label text-bronze text-xs">From {yacht.pricing4h.low}/4h</span>
                    <span className="font-body text-muted-foreground text-xs">· {yacht.length}</span>
                    <span className="font-body text-muted-foreground text-xs">· {yacht.guests}</span>
                  </div>
                  <span className="font-label text-foreground/60 text-[10px] border-b border-foreground/20 pb-0.5 group-hover:text-foreground group-hover:border-foreground/50 transition-colors duration-700">
                    View details
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-spacing bg-warm-black">
        <div className="container-luxury max-w-4xl">
          <motion.div
            className="text-center mb-16 md:mb-24"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">How It Works</span>
            <h2 className="font-display text-3xl md:text-5xl text-primary-foreground tracking-wide uppercase">
              Yacht Requests
            </h2>
            <p className="font-body text-primary-foreground/40 text-sm mt-4">
              A simple process, designed with ease in mind.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {[
              {
                step: "1",
                title: "Choose your yacht",
                text: "Browse our Marbella collection or simply send us a message. Whether you already have a yacht in mind or prefer guidance, our team will assist you in selecting the perfect vessel.",
              },
              {
                step: "2",
                title: "Share your preferences",
                text: "Let us know your desired date, route and group size. We check availability, confirm the price and arrange every detail — from catering to onboard entertainment.",
              },
              {
                step: "3",
                title: "Enjoy the experience",
                text: "Once confirmed, everything is prepared for your arrival at Puerto Banús. Step aboard, unwind, and let the Mediterranean set the rhythm of your day.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                className="text-center md:text-left"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: i * 0.15, ease }}
              >
                <span className="font-display text-5xl md:text-6xl text-primary-foreground/10 block mb-4">
                  {item.step}
                </span>
                <h3 className="font-display-medium text-lg text-primary-foreground tracking-wide uppercase mb-4">
                  {item.title}
                </h3>
                <p className="font-body text-primary-foreground/50 text-sm leading-[1.9]">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-6xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">FAQ</span>
            <h2 className="font-display text-3xl md:text-4xl text-foreground tracking-wide uppercase">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease }}
            className="grid grid-cols-1 md:grid-cols-2 gap-x-12"
          >
            <Accordion type="single" collapsible className="w-full">
              {leftFaq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-foreground/10">
                  <AccordionTrigger className="font-display-medium text-sm md:text-base text-foreground tracking-wide uppercase text-left hover:no-underline py-5">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-muted-foreground text-sm leading-[1.9]">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <Accordion type="single" collapsible className="w-full">
              {rightFaq.map((item, i) => (
                <AccordionItem key={i} value={`faq-r-${i}`} className="border-foreground/10">
                  <AccordionTrigger className="font-display-medium text-sm md:text-base text-foreground tracking-wide uppercase text-left hover:no-underline py-5">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-muted-foreground text-sm leading-[1.9]">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <p className="font-display-medium text-lg md:text-xl text-foreground tracking-wide uppercase mb-4">
              Still have a question?
            </p>
            <p className="font-body text-muted-foreground text-sm leading-[1.9] mb-8">
              Our team is happy to help with anything you need.
            </p>
            <a
              href="https://wa.me/message/2XEKJ5STR2IZJ1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
            >
              Contact Us
            </a>
          </motion.div>
        </div>
      </section>

      {/* Request CTA */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">Get In Touch</span>
            <h2 className="font-display text-3xl md:text-4xl text-foreground tracking-wide uppercase mb-6">
              Request a Yacht
            </h2>
            <p className="font-body text-muted-foreground text-sm leading-[1.9] mb-10">
              Send us a message with your preferred yacht, dates, and any special requests.
              Our team will respond promptly with availability and a tailored proposal.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://wa.me/message/2XEKJ5STR2IZJ1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
              >
                WhatsApp
              </a>
              <a
                href="mailto:info@arzanigroupworldwide.com"
                className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
              >
                Email Us
              </a>
            </div>
            <p className="font-body text-muted-foreground/60 text-xs mt-8">
              +31 6 19038233 · info@arzanigroupworldwide.com
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MarbellaYachts;
